import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';

type Profile = Database['public']['Tables']['profiles']['Row'];

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName?: string, phoneNumber?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: any }>;
  incrementUsage: (apiDelta: number, spaceDelta: number) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const cleanupAuthState = () => {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
        localStorage.removeItem(key);
      }
    });
  };

  const fetchProfile = async (userId: string, fullNameFromMeta?: string, phoneNumberFromMeta?: string) => {
    try {
      console.log('Fetching profile for user:', userId);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) {
        console.error('Error fetching profile:', error);
        // If profile doesn't exist, try to create one
        if (error.code === 'PGRST116') {
          console.log('Profile not found, creating one...');
          const { data: newProfile, error: createError } = await supabase
            .from('profiles')
            .insert({
              id: userId,
              email: user?.email || '',
              email_verified: user?.email_confirmed_at ? true : false,
              role: 'user',
              full_name: fullNameFromMeta || '',
              phone_number: phoneNumberFromMeta || '',
            })
            .select()
            .single();
          
          if (createError) {
            console.error('Error creating profile:', createError);
            return;
          }
          
          console.log('Profile created successfully:', newProfile);
          setProfile(newProfile);
        }
        return;
      }
      // If profile exists but full_name or phone_number is missing, update from user_metadata if available
      let updated = false;
      const updates: any = {};
      if ((!data.full_name || data.full_name === '') && fullNameFromMeta) {
        updates.full_name = fullNameFromMeta;
        data.full_name = fullNameFromMeta;
        updated = true;
      }
      if ((!data.phone_number || data.phone_number === '') && phoneNumberFromMeta) {
        updates.phone_number = phoneNumberFromMeta;
        data.phone_number = phoneNumberFromMeta;
        updated = true;
      }
      if (updated) {
        await supabase.from('profiles').update(updates).eq('id', userId);
      }
      console.log('Profile fetched successfully:', data);
      setProfile(data);
    } catch (error) {
      console.error('Error in fetchProfile:', error);
    }
  };

  useEffect(() => {
    console.log('Setting up auth state change listener...');
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state change:', event, 'Session exists:', !!session, 'User email:', session?.user?.email);
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          const fullNameFromMeta = session.user.user_metadata?.full_name;
          const phoneNumberFromMeta = session.user.user_metadata?.phone_number;
          console.log('User authenticated, fetching profile...');
          setTimeout(() => {
            fetchProfile(session.user.id, fullNameFromMeta, phoneNumberFromMeta);
          }, 0);
        } else {
          console.log('No user session, clearing profile');
          setProfile(null);
        }
        
        setLoading(false);
      }
    );

    // Check for existing session
    console.log('Checking for existing session...');
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Existing session check:', !!session, session?.user?.email);
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const fullNameFromMeta = session.user.user_metadata?.full_name;
        const phoneNumberFromMeta = session.user.user_metadata?.phone_number;
        setTimeout(() => {
          fetchProfile(session.user.id, fullNameFromMeta, phoneNumberFromMeta);
        }, 0);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName: string, phoneNumber: string) => {
    if (!email || !password || !fullName || !phoneNumber) {
      return { error: { message: 'All fields are required.' } };
    }
    try {
      console.log('Starting sign up for:', email);

      cleanupAuthState();
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        // Continue even if this fails
      }

      const redirectUrl = `${window.location.origin}/`;
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
            phone_number: phoneNumber
          }
        }
      });

      if (error) {
        // Handle case where user already exists
        if (error.message.includes('User already registered')) {
          return { error: { message: 'A user with this email already exists.' } };
        }
        return { error };
      }

      if (!error && data.user) {
        await supabase.from('profiles').update({
          full_name: fullName,
          phone_number: phoneNumber,
          email: email
        }).eq('id', data.user.id);
      }

      console.log('Sign up result:', error ? 'Error' : 'Success', error?.message);
      return { error };
    } catch (error) {
      console.error('Sign up error:', error);
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      console.log('Starting sign in for:', email);
      cleanupAuthState();
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        // Continue even if this fails
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.log('Sign in error:', error.message);
        return { error };
      }

      if (data.user && !data.user.email_confirmed_at) {
        await supabase.auth.signOut();
        const customError = {
          name: 'EmailNotConfirmed',
          message: 'Please verify your email before signing in. Check your inbox for the verification link.',
        };
        console.log('Sign in failed: Email not confirmed');
        return { error: customError };
      }

      console.log('Sign in successful, user:', data.user?.email);
      return { error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { error };
    }
  };

  const signOut = async () => {
    try {
      cleanupAuthState();
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        // Continue even if this fails
      }
      // Just clear state, do not refresh
      setUser(null);
      setSession(null);
      setProfile(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return { error: 'No user logged in' };
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (!error && profile) {
        setProfile({ ...profile, ...updates });
      }

      return { error };
    } catch (error) {
      return { error };
    }
  };

  const incrementUsage = async (apiDelta: number, spaceDelta: number) => {
    if (!user) return;
    // Optimistically update UI immediately
    setProfile(prev => prev ? {
      ...prev,
      api_calls_used: (prev.api_calls_used || 0) + (apiDelta || 0),
      monthly_space_used: (prev.monthly_space_used || 0) + (spaceDelta || 0),
    } : prev as any);

    // Persist in background
    try {
      await supabase
        .from('profiles')
        .update({
          api_calls_used: (profile?.api_calls_used || 0) + (apiDelta || 0),
          monthly_space_used: (profile?.monthly_space_used || 0) + (spaceDelta || 0),
        })
        .eq('id', user.id);
    } catch (e) {
      console.error('Failed to persist usage update:', e);
    }
  };

  const value = {
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    updateProfile,
    incrementUsage,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
